"use client";

import MainHeader from "@/components/customs/MainHeader";
import MainLayoutWithNavigationPending from "@/components/customs/navigation-pending/MainLayoutWithNavigationPending";
import NavigationPendingRoot from "@/components/customs/navigation-pending/NavigationPendingRoot";
import { getCookieSync, getSessionApiKeySync } from "@/lib/cookies";
import { type SupportedLanguage } from "@/lib/shared";
import { resolveLocale } from "@/lib/resolvedLocale";
import type { TRole } from "@/actions/types";
import { SignalRProvider } from "@/lib/signalR";
import * as signalR from "@microsoft/signalr";
import { useEffect, useLayoutEffect, useMemo, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { useParams } from "next/navigation";
import { AUTH_SESSION_CHANGED } from "@/lib/authSession";

export default function MainShellLayout() {
  const params = useParams();
  const location = useLocation();
  const routeLocale = useMemo(
    (): SupportedLanguage => resolveLocale(params.locale as string | undefined),
    [params.locale],
  );

  const [locale, setLocale] = useState<SupportedLanguage>(routeLocale);
  const [xApiKey, setXApiKey] = useState<string | undefined>(() =>
    typeof document !== "undefined" ? getSessionApiKeySync() : undefined,
  );
  const [role, setRole] = useState<TRole | undefined>(() => {
    const r = typeof document !== "undefined" ? getCookieSync("role") : undefined;
    return r as TRole | undefined;
  });

  useLayoutEffect(() => {
    setLocale(routeLocale);
    setXApiKey(getSessionApiKeySync());
    const r = getCookieSync("role");
    setRole(r as TRole | undefined);
  }, [location.pathname, location.key, routeLocale]);

  useEffect(() => {
    const refresh = () => {
      setXApiKey(getSessionApiKeySync());
      const r = getCookieSync("role");
      setRole(r as TRole | undefined);
    };
    window.addEventListener(AUTH_SESSION_CHANGED, refresh);
    return () => window.removeEventListener(AUTH_SESSION_CHANGED, refresh);
  }, []);

  const hubBase = import.meta.env.VITE_NOTIFICATION_HUB_URL;

  return (
    <SignalRProvider
      autoConnect={!!xApiKey}
      connectionUrl={
        xApiKey && hubBase ? `${hubBase}/notificationHub` : undefined
      }
      connectionOptions={
        xApiKey
          ? {
              withCredentials: true,
              transport:
                signalR.HttpTransportType.WebSockets |
                signalR.HttpTransportType.LongPolling,
              headers: { "X-API-KEY": xApiKey },
              skipNegotiation: false,
            }
          : undefined
      }
    >
      <main className="flex min-h-screen flex-col bg-gray-100">
        <NavigationPendingRoot>
          <div className="flex min-h-0 flex-1 flex-col">
            <div className="sticky top-0 z-40 shrink-0">
              <MainHeader />
            </div>
            <MainLayoutWithNavigationPending locale={locale} role={role}>
              <Outlet />
            </MainLayoutWithNavigationPending>
          </div>
        </NavigationPendingRoot>
      </main>
    </SignalRProvider>
  );
}
